import {
  DUPLICATE_MATCH_LABELS,
  type DuplicateCandidate,
  type Page as PageDto,
  PERMISSIONS,
  type ResolveDuplicateInput,
} from '@edms/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CopyCheck, FileCheck2, Replace, SplitSquareHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { PageBody, PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/ui/data-table';
import { Dialog, useToast } from '@/components/ui/overlays';
import { Button, Field, Textarea } from '@/components/ui/primitives';
import { Badge, Card, EmptyState, SectionHeader, Skeleton } from '@/components/ui/surfaces';
import { useAuth } from '@/features/auth/auth-context';
import { api, ApiError, fetchBlobUrl } from '@/lib/api-client';
import { formatDate, formatDateTime, formatPercent } from '@/lib/utils';
import { useEffect } from 'react';

type Resolution = ResolveDuplicateInput['resolution'];

const RESOLUTION_LABELS: Record<Resolution, string> = {
  IGNORED: 'Ignorer le nouveau document',
  REPLACED: "Remplacer l'archive existante",
  DISTINCT: 'Ce sont deux documents distincts',
};

const RESOLUTION_HELP: Record<Resolution, string> = {
  IGNORED:
    "Le document entrant est écarté ; l'archive déjà en place fait foi. C'est le choix habituel quand un reçu a été numérisé deux fois.",
  REPLACED:
    "Le document entrant devient l'archive de référence ; l'ancien est écarté. À retenir si la nouvelle numérisation est de meilleure qualité.",
  DISTINCT:
    "Le rapprochement est un faux positif : les deux documents sont conservés. L'historique de la décision est enregistré.",
};

/**
 * Arbitrage des doublons (§7).
 *
 * Le cahier des charges exige une comparaison **visuelle** avant décision : les
 * deux documents sont donc affichés côte à côte, pas seulement leurs
 * métadonnées. Aucune suppression n'a lieu — le document écarté change de
 * statut et reste traçable.
 */
export function DuplicatesPage(): React.JSX.Element {
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<{ candidate: DuplicateCandidate; resolution: Resolution } | null>(
    null,
  );

  const { data, isLoading } = useQuery({
    queryKey: ['duplicates', page],
    queryFn: () => api.get<PageDto<DuplicateCandidate>>('/receipts/duplicates', { page, pageSize: 10 }),
    placeholderData: keepPreviousData,
  });

  const candidates = data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Doublons détectés"
        description="Comparez les documents rapprochés avant de trancher"
        actions={
          data && data.total > 0 ? (
            <Badge tone="warning" dot>
              {data.total} à traiter
            </Badge>
          ) : undefined
        }
      />

      <PageBody>
        {isLoading ? (
          <Card className="space-y-4">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-56 w-full" />
          </Card>
        ) : candidates.length === 0 ? (
          <Card>
            <EmptyState
              icon={FileCheck2}
              title="Aucun doublon en attente"
              description="Le système compare l'empreinte des fichiers, la clé métier et la ressemblance visuelle de chaque document archivé."
            />
          </Card>
        ) : (
          <div className="space-y-4">
            {candidates.map((candidate) => (
              <DuplicateCard
                key={candidate.id}
                candidate={candidate}
                canResolve={can(PERMISSIONS.DUPLICATES_RESOLVE)}
                onResolve={(resolution) => setPending({ candidate, resolution })}
              />
            ))}

            {data && (
              <Card padded={false}>
                <Pagination
                  page={data.page}
                  pageSize={data.pageSize}
                  total={data.total}
                  totalPages={data.totalPages}
                  onPageChange={setPage}
                  className="border-t-0"
                />
              </Card>
            )}
          </div>
        )}
      </PageBody>

      {pending && (
        <ResolutionDialog
          candidate={pending.candidate}
          resolution={pending.resolution}
          onClose={() => setPending(null)}
        />
      )}
    </>
  );
}

function DuplicateCard({
  candidate,
  canResolve,
  onResolve,
}: {
  candidate: DuplicateCandidate;
  canResolve: boolean;
  onResolve: (resolution: Resolution) => void;
}): React.JSX.Element {
  return (
    <Card>
      <SectionHeader
        title={DUPLICATE_MATCH_LABELS[candidate.matchType]}
        description={`Détecté ${formatDateTime(candidate.detectedAt)} · similarité ${formatPercent(candidate.score)}`}
        actions={
          <Badge tone={candidate.matchType === 'PERCEPTUAL' ? 'warning' : 'danger'} dot>
            {formatPercent(candidate.score)}
          </Badge>
        }
      />

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <ComparisonPane title="Document entrant" receipt={candidate.incoming} tone="warning" />
        <ComparisonPane title="Archive existante" receipt={candidate.existing} tone="success" />
      </div>

      {canResolve && (
        <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-line-subtle pt-4">
          <Button
            size="sm"
            icon={<SplitSquareHorizontal className="size-4" />}
            onClick={() => onResolve('DISTINCT')}
          >
            Documents distincts
          </Button>
          <Button size="sm" icon={<Replace className="size-4" />} onClick={() => onResolve('REPLACED')}>
            Remplacer l'existant
          </Button>
          <Button
            size="sm"
            variant="primary"
            icon={<Trash2 className="size-4" />}
            onClick={() => onResolve('IGNORED')}
          >
            Ignorer le nouveau
          </Button>
        </div>
      )}
    </Card>
  );
}

function ComparisonPane({
  title,
  receipt,
  tone,
}: {
  title: string;
  receipt: DuplicateCandidate['incoming'];
  tone: 'warning' | 'success';
}): React.JSX.Element {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    let url: string | null = null;

    void fetchBlobUrl(`/receipts/${receipt.id}/pages/1?size=thumb`)
      .then((objectUrl) => {
        if (revoked) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        url = objectUrl;
        setImageUrl(objectUrl);
      })
      .catch(() => setImageUrl(null));

    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [receipt.id]);

  return (
    <div className="overflow-hidden rounded-lg border border-line-subtle">
      <div className="flex items-center justify-between gap-2 border-b border-line-subtle bg-sunken/60 px-3 py-2">
        <span className="text-xs font-medium text-ink">{title}</span>
        <Badge tone={tone}>{receipt.branchCode}</Badge>
      </div>

      <div className="flex gap-3 p-3">
        <div className="flex h-32 w-24 shrink-0 items-center justify-center overflow-hidden rounded border border-line-subtle bg-white">
          {imageUrl ? (
            <img src={imageUrl} alt={`Aperçu de ${receipt.searchKey}`} className="h-full object-contain" />
          ) : (
            <CopyCheck className="size-5 text-ink-faint" aria-hidden />
          )}
        </div>

        <dl className="min-w-0 flex-1 space-y-1.5 text-xs">
          <Row label="Numéro" value={receipt.orderNumber ?? receipt.searchKey} mono />
          <Row label="Sous-numéro" value={receipt.subNumber ?? '—'} mono />
          <Row label="Livraison" value={formatDate(receipt.deliveryDate)} />
          <Row label="Client" value={receipt.customerName ?? '—'} />
          <Row label="Lot" value={receipt.lotReference} mono />
          <Row label="Archivé" value={receipt.archivedAt ? formatDate(receipt.archivedAt) : 'non archivé'} />
        </dl>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }): React.JSX.Element {
  return (
    <div className="flex justify-between gap-2">
      <dt className="shrink-0 text-ink-faint">{label}</dt>
      <dd className={`truncate text-ink ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}

function ResolutionDialog({
  candidate,
  resolution,
  onClose,
}: {
  candidate: DuplicateCandidate;
  resolution: Resolution;
  onClose: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const resolve = useMutation({
    mutationFn: () =>
      api.post(`/receipts/duplicates/${candidate.id}/resolve`, {
        resolution,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      toast.success('Doublon tranché', RESOLUTION_LABELS[resolution]);
      void queryClient.invalidateQueries({ queryKey: ['duplicates'] });
      void queryClient.invalidateQueries({ queryKey: ['lot'] });
      onClose();
    },
    onError: (error) =>
      toast.error('Décision refusée', error instanceof ApiError ? error.message : undefined),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={RESOLUTION_LABELS[resolution]}
      description={`Document ${candidate.incoming.searchKey} · ${DUPLICATE_MATCH_LABELS[candidate.matchType]}`}
      footer={
        <>
          <Button onClick={onClose}>Annuler</Button>
          <Button
            variant={resolution === 'IGNORED' ? 'danger' : 'primary'}
            loading={resolve.isPending}
            onClick={() => resolve.mutate()}
          >
            Confirmer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="rounded-md border border-line-subtle bg-sunken/60 px-3 py-2.5 text-xs leading-relaxed text-ink-muted">
          {RESOLUTION_HELP[resolution]}
        </p>

        <Field
          label="Motif"
          htmlFor="resolution-note"
          hint="Conservé dans le journal d'audit avec votre nom et l'horodatage."
        >
          <Textarea
            id="resolution-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Reçu numérisé deux fois lors de la session du matin."
            maxLength={280}
          />
        </Field>
      </div>
    </Dialog>
  );
}
